/**
 * CONTACT-MGMT (CM-5/CM-6) — matrix-driven access gates for the contacts
 * address book.
 *
 * READ (CM-D1): every role except `pilot`. Driven off the authoritative
 * `contact.read` role-matrix cell (`useRoleMatrixContext`) so it stays
 * truthful; falls back to the canonical role set (every role except `pilot`,
 * via the sanctioned `useHasRole` identity escape hatch) when the matrix is
 * still loading or predates the `contact.*` keys.
 *
 * WRITE (CM-D1): add / edit / delete / restore are gated to
 * `tenant_admin` / `project_manager` / `back_office`. Driven off the
 * `contact.create|update|delete` cells via `useCanEditResourceFor` (which
 * resolves a `"conditional"` within-tenant cell optimistically — the same UX
 * nicety the Sites Add button uses; the BE remains the security boundary).
 */
import { useCanEditResourceFor, useRoleMatrixContext } from "@aeriosiq/ui";
import { useHasRole } from "@aeriosiq/auth-client";

/**
 * True when the caller may read the address book (every role except
 * `pilot`). Returns `false` while no role has resolved yet
 * (safe-default-deny).
 */
export function useCanReadContacts(): boolean {
  const { role, matrix } = useRoleMatrixContext();
  const isPilot = useHasRole("pilot");
  if (!role) return false;
  // `grid` is a declared (required) property on RoleMatrix, but a partial
  // matrix can arrive at runtime (e.g. a degraded `/role-matrix` payload),
  // so guard it before the indexed lookups (which are `| undefined` under
  // noUncheckedIndexedAccess).
  const grid = matrix?.grid;
  const cell = grid ? grid[role]?.["contact.read"] : undefined;
  if (cell === true) return true;
  if (cell === false) return false;
  // Cell absent (matrix loading, or an older matrix without contact.*) —
  // fall back to the canonical role set: read = everyone except pilot.
  return !isPilot;
}

export interface ContactWriteAccess {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

/**
 * The contact write affordances available to the caller. Restore shares the
 * delete authority (CM-D15), so callers gate Restore on `canDelete`.
 */
export function useContactWriteAccess(): ContactWriteAccess {
  return {
    canCreate: useCanEditResourceFor("contact.create"),
    canUpdate: useCanEditResourceFor("contact.update"),
    canDelete: useCanEditResourceFor("contact.delete"),
  };
}
